import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/cms/ui/input';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/cms/ui/dialog';
import { useAuthUser } from '@/hooks/useAuthUser';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import LureoButton from '@/common/LureoButton';

interface NewProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NewProjectDialog({ open, onOpenChange }: NewProjectDialogProps) {
  const navigate = useNavigate();
  const { user } = useAuthUser();
  const services = useServices();
  const [projectName, setProjectName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !projectName.trim()) return;

    setIsCreating(true);

    try {
      await services.project.createNewProject(user.id, navigate, projectName.trim());
      // Reset form
      setProjectName('');
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating project:', error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="cursor-default select-none">
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
            <DialogDescription>Give your project a name to start storytelling in AR.</DialogDescription>
          </DialogHeader>

          <DialogBody>
            <Input
              id="project-name"
              aria-label="Project name"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Enter name..."
              disabled={isCreating}
              autoFocus
            />
          </DialogBody>

          <DialogFooter>
            <LureoButton type="submit" variant="secondary" suppressTooltip disabled={!projectName.trim() || isCreating} className="h-9 px-4 rounded-lureo-radius-button-pill">
              Create Project
            </LureoButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
